import React, { useEffect, useState, useMemo, useContext } from 'react';
import { useRouteMatch } from 'react-router-dom';
import classnames from 'classnames';
import { FormattedHTMLMessage } from 'react-intl';
import { useGuideConfig, useDispatch, triggerIgniteAction, useNeuroState, AnswersList, NeuroMessage, ShareViaEmailModal, ShareViaSmsModal, usePages } from "@ignitesales/neuro-application";
import ContactMeConfirmationModal from '../ContactMeConfirmationModal';
import Product from '../Product';
import CompleteGuideFlow from '../CompleteGuideFlow';
import { sendResultsViaEmail, contactLeadUpdate } from "@ignitesales/neuro-application/src/neuro-guide/state/ContactMe/ContactMeActions.mjs";

import ReactSVG from 'react-svg';

function SummaryPage(props) {
  const {
    classname,
    detailsUrl,
    productsList,
  } = props;
  const cssBEM = 'summaryPage';
  const { answers, user } = useNeuroState();
  const dispatch = useDispatch();

  const actionCompleted = (actionName, productId, value, append) => {
    dispatch(triggerIgniteAction({actionName, productId, value, append}));
  };

  useEffect(() => {
    stickyElementScrollHandler();
    window.addEventListener('scroll', stickyElementScrollHandler);
    return () => {
      window.removeEventListener('scroll', stickyElementScrollHandler);
    };
  }, []);

  const classes = classnames(cssBEM, classname);
  const { isBranchGuide } = useGuideConfig();
  const matchDetailsPage = useRouteMatch('/guide/:uuid/details');
  const { goToPreviousPage } = usePages();
  const [sticky, setSticky] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [shareViaEmailOpen, setShareViaEmailOpen] = useState(false);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactConfirmationOpen, setContactConfirmationOpen] = useState(false);
  const productsRecommendedList = productsList;
  const onPrintClick = () => {
    actionCompleted('printButtonClicked');
    window.focus();
    window.print(detailsUrl);
  };

  const stickyElementScrollHandler = () => {
    const threshold = 300;
    const position = document.getElementsByTagName('html')[0].scrollTop;
    setSticky(position >= threshold);
  };

  const renderProduct = (product) => {
    return (
      <Product
        key={product.id}
        product={product}
        productActionCompleted={actionCompleted}
        leadInfo={{
          sent: answers?.leadTransmitted,
          openContactForm: () => completeConversationClicked(true),
          openContactConfirmationModal: () => setContactConfirmationOpen(true),
        }}
      />
    );
  };

  const memoProducts = useMemo(
    () => productsRecommendedList.map((p) => renderProduct(p)),
    [productsList]
  )

  const completeConversationClicked = () => {
    if (!answers?.leadTransmitted) {
      actionCompleted('contactMeClicked');
      setContactFormOpen(true);

      const firstName = document.querySelector('.firstName input');
      if (firstName) {
        firstName.focus();
      }
    } else {
      setContactConfirmationOpen(true);
    }
  };

  const contactFormSubmission = (lead, shouldSendToCustomer) => {
    const loc = 'contactFormSubmission';
    try {
    setContactSubmitted(true);
    dispatch(contactLeadUpdate({contactLead: lead}));
    console.log(loc, `isBranchGuide: ${isBranchGuide}`);
    console.log(loc, `shouldSendToCustomer: ${shouldSendToCustomer}`);
    shouldSendToCustomer && dispatch(sendResultsViaEmail({ email: lead.email }));
    } catch (error) {
      console.error(loc, error)
    }
  };

  const hasResults = productsRecommendedList !== undefined && productsRecommendedList.length !== 0



  return (
      <div className={classes}>
        {!matchDetailsPage && (
          <button
            onClick={goToPreviousPage}
            className="btn__back"
            role="button"
            aria-label="Go Back"
          >
            <i className="fa fa-caret-left" aria-hidden="true" />
            <NeuroMessage id="summary.back" />
          </button>
        )}

        <div className={`${cssBEM}__image`}>
          <img src="/classpath/assets/images/summary-mobile.jpg" />
        </div>

        <div className={`${cssBEM}__container`}>

          <div className={`${cssBEM}__social-actions`}>
            <button className="roundIcon" onClick={() => setSmsOpen(true)}>
              <i className="fa fa-mobile"></i>
            </button>
            <button
              className="roundIcon"
              onClick={() => setShareViaEmailOpen(true)}
            >
              <i className="fa fa-envelope"></i>
            </button>
            <button className="roundIcon" onClick={onPrintClick}>
              <i className="fa fa-print"></i>
            </button>
          </div>

          {
            /* Check if no products returns at all */
            !hasResults &&
            <div className={`${cssBEM}__norecommendations`}>
              <FormattedHTMLMessage
                id="summary.norecommendations"
              />
            </div>
          }


          {user?.uuid && (
            <div className={`${cssBEM}__user`}>
              <span>UUID: {user.uuid}</span>
            </div>
          )}

          {
            /* Check if there are some product recommended */
            hasResults &&
            <div className={`${cssBEM}__header ${sticky ? 'sticky' : ''}`}>
              <NeuroMessage id="summary.recommendations.title" tagName="h2" />
              <NeuroMessage
                id="summary.recommendations.subtitle"
                tagName="p"
              />
            </div>
          }

          {!isBranchGuide && hasResults && (
            <div className={`${cssBEM}__complete`}>
              <button
                className="btn btn-primary action"
                onClick={() => completeConversationClicked(true)}
                role="button"
                aria-label="complete action"
              >
                <NeuroMessage id="summary.completeConsumerOnline" />
              </button>
            </div>
          )}

          {hasResults &&
          <>
          <div className={`${cssBEM}__actions ${sticky ? 'sticky' : ''}`}>
            <div className={`${cssBEM}__actions-wrapper`}>
              <div className="action-bar">
                <NeuroMessage
                  id="summary.actions.answersTitle"
                  tagName="h3"
                />
                <button
                  className="btn action action--answersList"
                  onClick={() =>
                    document.getElementById('answersSection').scrollIntoView()
                  }
                  role="button"
                  aria-label="View Answers"
                >
                  <NeuroMessage id="summary.actions.answers" />
                  <i className="fa fa-angle-right" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </div>

          <div className={`${cssBEM}__products`}>
            {memoProducts}
          </div>

          <div className={`${cssBEM}__complete`}>
            <button
              className="btn btn-primary action"
              onClick={() => completeConversationClicked(true)}
              role="button"
              aria-label="complete action"
            >
            {
              !isBranchGuide ?
                <NeuroMessage id="summary.completeConsumerOnline" />
              :
                <NeuroMessage id="summary.complete" />
            }
            </button>
          </div>
          </>
          }
          <div className={`${cssBEM}__footer`}>
            <div>
              <FormattedHTMLMessage id="summary.recommendations.footer" />
            </div>
            <div>
              <ReactSVG src="/classpath/assets/images/FDIC-EHL.svg" />
            </div>
          </div>
        </div>

        <div className={`${cssBEM}__answers`} id="answersSection">
          <div className={`${cssBEM}__container`}>
            <NeuroMessage id="summary.yourAnswers" tagName="h2" />
            <AnswersList answers={answers} actionCompleted={actionCompleted} />
          </div>
        </div>
        <CompleteGuideFlow
          contactFormSubmitted={contactSubmitted}
          contactFlowClicked={setContactFormOpen}
          formIsVisible={contactFormOpen}
          onCloseForm={() => setContactFormOpen(false)}
          onFormSubmission={(lead, shouldSendToCustomer) => contactFormSubmission(lead, shouldSendToCustomer)}
        />
        <ShareViaEmailModal isOpen={ shareViaEmailOpen } onClose={() => {
            setShareViaEmailOpen(false);
          }} className="shareViaEmail modal--dialog"/>
        <ShareViaSmsModal
          isOpen={ smsOpen } onClose={() => {
            setSmsOpen(false);
          }} className="shareViaSms modal--dialog"
        />
        <ContactMeConfirmationModal
          active={contactConfirmationOpen}
          onClose={() => {
            setContactConfirmationOpen(false);
          }}
        />
      </div>
  );
}

export default SummaryPage;
